import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'
import { BacklinkSummaryPanel } from '@/components/backlink-summary'
import { CoveragePanel } from '@/components/coverage-panel'
import { RANGE_LABELS, useDateWindow } from '@/components/date-range-picker'
import { EmptyState } from '@/components/empty-state'
import { KeywordPanel } from '@/components/keyword-panel'
import { PerformancePanel } from '@/components/performance-panel'
import { Button } from '@/components/ui/button'
import { useBacklinkSummary } from '@/lib/backlinks'
import type { DateRange } from '@/lib/queries'
import { useOverview } from '@/lib/site-data'
import { MetricsSkeleton, SectionHeader, useSiteScope } from './-section'

export const Route = createFileRoute('/_app/$org/$host/')({
  component: SiteOverviewPage,
})

/**
 * The site's front page: how it is doing, then what is wrong with it.
 *
 * Unlike every other report, this one does not gate the whole page on a
 * connected search source. The sitemap crawl needs nothing but the domain, so a
 * site added five minutes ago and never connected still has a real answer here
 * — and the page that would otherwise say "nothing connected yet" is exactly
 * the page where the first useful thing should appear.
 */
function SiteOverviewPage() {
  const { org, host, range, site } = useSiteScope()
  const { data, isPending } = useOverview(site, range)
  const backlinks = useBacklinkSummary(site)
  useDateWindow(data?.range)

  return (
    <>
      <SectionHeader title="Overview" />

      <div className="space-y-4">
        <Performance org={org} host={host} range={range} data={data} isPending={isPending} />

        {/* Cards below the chart share one grid so a second and third land
            beside Coverage rather than under it. The two summaries stack in one
            column because each is short and Coverage is not: three equal
            columns would leave the tallest panel dragging two empty boxes. */}
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <CoveragePanel site={site} host={host} range={range} />

          <div className="space-y-4">
            <KeywordPanel
              rows={data?.keywords}
              isPending={isPending}
              actions={
                <SectionLink to="/$org/$host/keywords" params={{ org, host }} range={range} />
              }
            />
            <BacklinkSummaryPanel
              data={backlinks.data}
              isPending={backlinks.isPending}
              actions={<SectionLink to="/$org/$host/backlinks" params={{ org, host }} />}
            />
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * The chart, or the reason there is no chart. Kept narrow deliberately: the
 * three dead ends belong to the search half of the page, and letting them
 * swallow the coverage panel below would hide the one thing that still works.
 */
function Performance({
  org,
  host,
  range,
  data,
  isPending,
}: {
  org: string
  host: string
  range: ReturnType<typeof useSiteScope>['range']
  data: ReturnType<typeof useOverview>['data']
  isPending: boolean
}) {
  if (isPending || !data) return <MetricsSkeleton />

  if (data.state === 'not-connected') {
    return (
      <Card>
        <EmptyState
          title="No search data yet"
          description={`${host} is being read for its own sitemaps already. Connect the account that owns it and the last 16 months of search performance fill in alongside.`}
          action={
            <Button asChild size="sm">
              <Link to="/$org/settings/connections" params={{ org }}>
                Connect a source
              </Link>
            </Button>
          }
        />
      </Card>
    )
  }

  if (data.series.length === 0) {
    return (
      <Card>
        <EmptyState
          title={data.state === 'backfilling' ? 'Collecting history' : 'Nothing in this window'}
          description={
            data.state === 'backfilling'
              ? `${host} is being read a day at a time. The first rows usually appear within a few minutes; the full 16 months takes longer.`
              : `No searches were recorded for ${host} over the ${RANGE_LABELS[range].toLowerCase()}. A wider range is the first thing to try.`
          }
        />
      </Card>
    )
  }

  return (
    <div className="space-y-2">
      <PerformancePanel
        totals={data.totals}
        series={data.series}
        previousSeries={data.previousSeries}
        bing={data.bing}
        rangeStart={data.range.from}
        previousStart={data.previousRange?.from}
      />
      <div className="flex justify-end">
        <Link
          to="/$org/$host/search"
          params={{ org, host }}
          search={{ range }}
          className="mono-label flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          Search detail
          <ArrowRightIcon className="size-3" aria-hidden />
        </Link>
      </div>
    </div>
  )
}

/** The panel header's own way out to the section it summarises. Backlinks take
 * no range, so the search param is optional rather than always carried. */
function SectionLink({
  to,
  params,
  range,
}: {
  to: '/$org/$host/keywords' | '/$org/$host/backlinks'
  params: { org: string; host: string }
  range?: DateRange
}) {
  return (
    <Link
      to={to}
      params={params}
      search={range ? { range } : undefined}
      className="mono-label flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
    >
      All
      <ArrowRightIcon className="size-3" aria-hidden />
    </Link>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-border bg-card">{children}</div>
}
